import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { apiErrorMessage } from '../api'
import { useDialogs } from '../components/Dialogs'
import {
  customersApi, occasionsApi, ordersApi, productsApi, ORDER_STATUS_LABEL,
  type DeliveryOccasion, type Order, type OrderRowInput, type ProductListItem,
} from '../crmApi'

interface RowState { productId: number; numberOfUnits: number }

export function OrderEdit() {
  const { confirm } = useDialogs()
  const { id } = useParams()
  const [search] = useSearchParams()
  const isNew = id === undefined
  const orderId = isNew ? undefined : Number(id)
  const navigate = useNavigate()

  const [customerId, setCustomerId] = useState<number>(Number(search.get('customerId')) || 0)
  const [customerName, setCustomerName] = useState('')
  const [products, setProducts] = useState<ProductListItem[]>([])
  const [occasions, setOccasions] = useState<DeliveryOccasion[]>([])
  const [occasionId, setOccasionId] = useState<number>(0)
  const [comment, setComment] = useState('')
  const [rows, setRows] = useState<RowState[]>([{ productId: 0, numberOfUnits: 1 }])
  const [existing, setExisting] = useState<Order | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  const priceById = useMemo(() => new Map(products.map((p) => [p.id, p.currentPrice ?? 0])), [products])

  useEffect(() => {
    productsApi.list(true).then(setProducts).catch((e) => setError(describe(e)))
    if (isNew) {
      if (!customerId) { setError('Ingen kund vald.'); return }
      customersApi.get(customerId).then((c) => {
        setCustomerName(`${c.firstName} ${c.lastName ?? ''}`.trim())
        return occasionsApi.list(c.deliveryAreaId, true)
      }).then((occ) => { setOccasions(occ); setLoaded(true) }).catch((e) => setError(describe(e)))
    } else {
      ordersApi.get(orderId!).then((o) => {
        setExisting(o); setCustomerId(o.customerId); setCustomerName(o.customerName)
        setOccasionId(o.deliveryOccasionId); setComment(o.comment ?? '')
        setRows(o.rows.length ? o.rows.map((r) => ({ productId: r.productId, numberOfUnits: r.numberOfUnits })) : [{ productId: 0, numberOfUnits: 1 }])
        setLoaded(true)
      }).catch((e) => setError(describe(e)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const editable = isNew || existing?.status === 'Open'
  const total = rows.reduce((sum, r) => sum + (priceById.get(r.productId) ?? 0) * r.numberOfUnits, 0)

  function setRow(i: number, patch: Partial<RowState>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  }

  async function save() {
    const validRows: OrderRowInput[] = rows.filter((r) => r.productId > 0 && r.numberOfUnits > 0)
    if (validRows.length === 0) { setError('Lägg till minst en orderrad.'); return }
    try {
      if (isNew) {
        if (!occasionId) { setError('Välj leveranstillfälle.'); return }
        const o = await ordersApi.place({ customerId, deliveryOccasionId: occasionId, comment: comment || null, rows: validRows })
        navigate(`/orders/${o.id}`)
      } else {
        await ordersApi.update(orderId!, { comment: comment || null, rows: validRows })
        navigate(`/customers/${customerId}`)
      }
    } catch (e) { setError(describe(e)) }
  }

  async function cancelOrder() {
    if (isNew) return
    if (!(await confirm('Makulera ordern?', { danger: true, confirmLabel: 'Makulera' }))) return
    try { await ordersApi.cancel(orderId!); navigate(`/customers/${customerId}`) }
    catch (e) { setError(describe(e)) }
  }

  if (!loaded) return <p className="muted">{error ?? 'Laddar…'}</p>

  return (
    <div>
      <div className="page-head">
        <h1>{isNew ? 'Lägg order' : `Order #${orderId}`} — {customerName}</h1>
        <button onClick={() => navigate(`/customers/${customerId}`)}>Tillbaka</button>
      </div>
      {error && <p className="error">{error}</p>}
      {existing && <p className="muted">Status: <span className="badge">{ORDER_STATUS_LABEL[existing.status]}</span></p>}

      <div className="card">
        <div className="form-grid">
          <label className="field"><span className="field-label">Leveranstillfälle *</span>
            {isNew ? (
              <select value={occasionId} onChange={(e) => setOccasionId(Number(e.target.value))}>
                <option value={0}>— välj —</option>
                {occasions.map((o) => <option key={o.id} value={o.id}>{o.date}</option>)}
              </select>
            ) : <input value={existing?.occasionDate ?? ''} disabled />}
          </label>
          <label className="field"><span className="field-label">Orderkommentar</span>
            <input value={comment} onChange={(e) => setComment(e.target.value)} disabled={!editable} />
          </label>
        </div>

        <h2>Orderrader</h2>
        <table className="grid">
          <thead><tr><th>Produkt</th><th>Antal</th><th>Á-pris</th><th>Summa</th><th></th></tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const price = priceById.get(r.productId) ?? 0
              return (
                <tr key={i}>
                  <td>
                    <select value={r.productId} disabled={!editable} onChange={(e) => setRow(i, { productId: Number(e.target.value) })}>
                      <option value={0}>— välj produkt —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td><input type="number" min={1} value={r.numberOfUnits} disabled={!editable}
                    onChange={(e) => setRow(i, { numberOfUnits: Number(e.target.value) })} style={{ width: '5rem' }} /></td>
                  <td>{price.toFixed(2)} kr</td>
                  <td>{(price * r.numberOfUnits).toFixed(2)} kr</td>
                  <td className="actions">
                    {editable && <button onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>Ta bort</button>}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot><tr><th colSpan={3} style={{ textAlign: 'right' }}>Totalt</th><th>{total.toFixed(2)} kr</th><th></th></tr></tfoot>
        </table>
        {editable && <button onClick={() => setRows((rs) => [...rs, { productId: 0, numberOfUnits: 1 }])} style={{ marginTop: '0.6rem' }}>+ Rad</button>}
      </div>

      {editable && (
        <div className="form-actions">
          <button className="primary" onClick={() => void save()}>Spara order</button>
          {!isNew && <button onClick={() => void cancelOrder()}>Makulera</button>}
        </div>
      )}
    </div>
  )
}

const describe = apiErrorMessage
